import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  DriveResource,
  DriveResourceCreateInput,
  DriveResourceUpdateInput,
  DriveSectionId
} from '../types';
import {
  createDriveResource,
  deleteDriveResource,
  listenToSectionResources,
  updateDriveResource
} from '../services/driveResourceService';

interface UseDriveResourcesOptions {
  section: DriveSectionId;
  enabled?: boolean;
  userId?: string;
}

interface UseDriveResourcesResult {
  resources: DriveResource[];
  loading: boolean;
  error: Error | null;
  createResource: (input: DriveResourceCreateInput) => Promise<void>;
  updateResource: (input: DriveResourceUpdateInput) => Promise<void>;
  deleteResource: (id: string) => Promise<void>;
}

export const useDriveResources = ({ section, enabled = true, userId }: UseDriveResourcesOptions): UseDriveResourcesResult => {
  const [resources, setResources] = useState<DriveResource[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setResources([]);
      return () => undefined;
    }

    setLoading(true);
    const unsubscribe = listenToSectionResources(
      section,
      (data) => {
        setResources(data);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [section, enabled]);

  const createResource = useCallback(
    async (input: DriveResourceCreateInput) => {
      if (!userId) {
        throw new Error('User ID required to create resources');
      }
      try {
        setLoading(true);
        await createDriveResource(input, userId);
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  const updateResourceHandler = useCallback(
    async (input: DriveResourceUpdateInput) => {
      if (!userId) {
        throw new Error('User ID required to update resources');
      }
      try {
        setLoading(true);
        await updateDriveResource(input, userId);
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  const deleteResourceHandler = useCallback(
    async (id: string) => {
      try {
        setLoading(true);
        await deleteDriveResource(id);
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return useMemo(
    () => ({
      resources,
      loading,
      error,
      createResource,
      updateResource: updateResourceHandler,
      deleteResource: deleteResourceHandler
    }),
    [resources, loading, error, createResource, updateResourceHandler, deleteResourceHandler]
  );
};

export default useDriveResources;
